import React, { useState } from 'react';
import {
  Box,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Alert,
  CircularProgress,
  IconButton
} from '@mui/material';
import {
  Support as SupportIcon,
  Close as CloseIcon,
  Send as SendIcon,
  WhatsApp as WhatsAppIcon,
  Email as EmailIcon
} from '@mui/icons-material';
import { useLocation } from 'react-router-dom';
import http from '../../http-common';
import { useTranslation } from '../../hooks/useTranslation';

const SupportChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [problem, setProblem] = useState('');
  const [contactMethod, setContactMethod] = useState('email');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const location = useLocation();
  const { t } = useTranslation();

  const currentUrl = window.location.origin + location.pathname;

  const handleOpen = () => {
    setOpen(true);
    setSuccess(false);
    setError('');
  };

  const handleClose = () => {
    setOpen(false);
    setProblem('');
    setContactMethod('email');
    setSuccess(false);
    setError('');
  };

  const handleSubmit = async () => {
    if (!problem.trim()) {
      setError(t('support.errorRequired'));
      return;
    }

    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      if (contactMethod === 'email') {
        // Send via email API
        await http.post('/support/contact', {
          problem: problem.trim(),
          url: currentUrl,
          method: 'email'
        });
        setSuccess(true);
        setTimeout(() => {
          handleClose();
        }, 2000);
      } else {
        // Open WhatsApp with pre-filled message
        const message = encodeURIComponent(
          t('support.whatsappMessage', {
            problem: problem.trim(),
            url: currentUrl
          })
        );
        const whatsappUrl = `https://wa.me/?text=${message}`;
        window.open(whatsappUrl, '_blank');
        setSuccess(true);
        setTimeout(() => {
          handleClose();
        }, 1500);
      }
    } catch (err) {
      setError(err.response?.data?.message || t('support.errorSend'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Floating Action Button */}
      <Fab
        color="primary"
        aria-label="support"
        onClick={handleOpen}
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          zIndex: 1000,
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          '&:hover': {
            boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
          }
        }}
      >
        <SupportIcon />
      </Fab>

      {/* Support Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          pb: 1
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SupportIcon color="primary" />
            <Typography variant="h6">{t('support.title')}</Typography>
          </Box>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {contactMethod === 'email' 
                ? t('support.successEmail')
                : t('support.successWhatsApp')}
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TextField
            autoFocus
            margin="dense"
            label={t('support.problemLabel')}
            placeholder={t('support.problemPlaceholder')}
            fullWidth
            multiline
            rows={4}
            value={problem}
            onChange={(e) => setProblem(e.target.value)}
            disabled={loading}
            sx={{ mb: 3 }}
          />

          <TextField
            margin="dense"
            label={t('support.urlLabel')}
            fullWidth
            value={currentUrl}
            disabled
            sx={{ mb: 3 }}
            InputProps={{
              readOnly: true,
            }}
          />

          <FormControl component="fieldset" fullWidth>
            <FormLabel component="legend" sx={{ mb: 1 }}>
              {t('support.contactMethodLabel')}
            </FormLabel>
            <RadioGroup
              value={contactMethod}
              onChange={(e) => setContactMethod(e.target.value)}
            >
              <FormControlLabel
                value="email"
                control={<Radio />}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EmailIcon fontSize="small" color="primary" />
                    <Typography>{t('support.sendViaEmail')}</Typography>
                  </Box>
                }
                disabled={loading}
              />
              <FormControlLabel
                value="whatsapp"
                control={<Radio />}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WhatsAppIcon fontSize="small" sx={{ color: '#25D366' }} />
                    <Typography>{t('support.openWhatsApp')}</Typography>
                  </Box>
                }
                disabled={loading}
              />
            </RadioGroup>
          </FormControl>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} disabled={loading}>
            {t('support.cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            startIcon={loading ? <CircularProgress size={16} /> : <SendIcon />}
            disabled={loading || !problem.trim()}
          >
            {loading ? t('support.sending') : t('support.submit')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SupportChatWidget;
